import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom'; 
import { useAuth } from '../../contexts/AuthContext';
import StudentLayout from '../../components/layout/StudentLayout';
import { 
  Typography, 
  Box, 
  Container, 
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  CircularProgress,
  Chip,
  Tooltip,
  LinearProgress,
  IconButton,
  Snackbar,
  Alert,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Home as HomeIcon,
  Close as CloseIcon,
  Add as AddIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import testService from '../../services/testService';
import { getCurrentSubscription } from '../../services/subscriptionService';
import { TEST_TYPES } from '../../config/Config';
import { filterPublished } from '../../utils/publishedFilter';

import UploadModal from '../../components/tests/UploadModal.jsx';
import AutoTestDialog from '../../components/tests/AutoTestDialog.jsx';
import { FixedSizeList } from 'react-window';
import TestCard from '../../components/tests/TestCard.jsx';
import uploadService from '../../services/uploadService';
import api from '../../services/api';
import { TEST_ATTEMPT_NOTE, ATTEMPTS, REVISIONS, STUDENT_ASSIGNMENTS } from '../../config/Config';
import PdfAnnotationViewer from '../../components/tests/PdfAnnotationViewer';
import MathRenderer from '../../components/common/MathRenderer';

const Tests = () => {
  const location = useLocation(); 
  const { user } = useAuth();
  const studentId = user?.id || localStorage.getItem('userId');
  const [uploadDialog, setUploadDialog] = useState({ open: false, testId: null, attemptId: null });
  const [checkWorksheetDialog, setCheckWorksheetDialog] = useState({
    open: false,
    uploadId: null,
    filename: null,
    attemptId: null,
    questions: [],
    revisionData: [],
    gradingDone: false,
  });
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'all'); 
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [testUploads, setTestUploads] = useState({}); // Map of testId -> uploads array
  // Assignments returned by STUDENT_ASSIGNMENTS but not by STUDENT_AVAILABLE_TESTS (e.g. unpublished tests)
  // so Assigned tab matches dashboard "Tests to Complete"
  const [assignmentsSupplement, setAssignmentsSupplement] = useState([]);
  // Submitted attempts whose test is not in the main API list (e.g. tests with subquestions when API omits them)
  const [completedSupplement, setCompletedSupplement] = useState([]);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'info' 
  });

  /** Normalize test id for consistent comparison (API may return number or string). */
  const normalizeTestId = (id) => (id == null ? '' : String(id));

  /** Single source of truth: test is completed (submitted) regardless of main vs subquestion answers. */
  const isTestCompleted = (test) => {
    if (!test) return false;
    if (test.has_submitted_attempt === true) return true;
    const s = String(test.status || '').toLowerCase();
    const a = String(test.attempt_status || '').toLowerCase();
    return s === 'submitted' || s === 'completed' || a === 'completed';
  };
  
  const [resumeDialog, setResumeDialog] = useState({
    open: false,
    test: null,
    ongoing: null,
  });
  const [downloadingWorksheet, setDownloadingWorksheet] = useState(false);

  // Subscription state
  const [userSubscription, setUserSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Auto test generation state
  const [openAutoDialog, setOpenAutoDialog] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [years, setYears] = useState([]);
  const [boards, setBoards] = useState([]);
  const handleResumeConfirm = () => {
    const { test, ongoing } = resumeDialog;
    setResumeDialog({ open: false, test: null, ongoing: null });
  
    navigate(`/test/${test.id}`, {
      state: {
        attemptId: ongoing.attempt_id,
        questionIndex: ongoing.question_index,
        mode: ongoing.mode,
      }
    });
  };
  
  const handleResumeCancel = () => {
    setResumeDialog({ open: false, test: null, ongoing: null });
    navigate('/tests');
  };

  const handleDownloadWorksheetFromResume = async () => {
    const { test } = resumeDialog;
    if (!test?.id) return;
    try {
      setDownloadingWorksheet(true);
      await testService.downloadTestWorksheet(test.id, test.title || 'worksheet');
      setSnackbar({ open: true, message: 'Worksheet downloaded successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.status ? `Failed to download worksheet: ${err.response.status}` : (err.message || 'Error downloading worksheet. Please try again.'),
        severity: 'error',
      });
    } finally {
      setDownloadingWorksheet(false);
    }
  };

  // Results dialog state
  const [resultsDialog, setResultsDialog] = useState({
    open: false,
    loading: false,
    results: [],
    testTitle: '',
    page: 0,
    rowsPerPage: 10
  });

  
  // Fetch user subscription
  const fetchUserSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      const subscription = await getCurrentSubscription();
      setUserSubscription(subscription);
    } catch (error) {
      setUserSubscription(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Fetch subscription on component mount
  useEffect(() => {
    fetchUserSubscription();
  }, []);

  // Fetch tests from the API based on active tab
  useEffect(() => {
    if (location.state?.tab) {
      window.history.replaceState({}, document.title);  // Clear navigation state
    }

    

    const fetchTests = async () => {
      try {
        setLoading(true);
        
        // Map tab names to status parameters for the API
        const statusMap = {
          'all': 'all',
          'assigned': 'active',
          'completed': 'completed',
          'not-started': 'active'
        };
        
        const status = statusMap[activeTab] || 'all';
        
        // Get the current user ID for completed tests
        const userId = localStorage.getItem('userId');
        
        // Get tests with attempt data
        const testsData = await testService.getTests({ 
          status,
          userId
        });
        
        // Backend returns has_upload and is_marked per test; no per-test API calls needed for main list
        const transformedTests = Array.isArray(testsData) ? testsData : [];
        setTests(transformedTests);

        // Supplement: assignments/attempts not in API list so All/Assigned/Completed tabs show correct items
        try {
          const params = {};
          const [assignmentsRes, attemptsRes] = await Promise.all([
            api.get(STUDENT_ASSIGNMENTS, { params }),
            api.get(ATTEMPTS, { params })
          ]);
          const assignmentsList = Array.isArray(assignmentsRes.data) ? assignmentsRes.data : (assignmentsRes.data?.results || []);
          const attemptsList = Array.isArray(attemptsRes.data) ? attemptsRes.data : (attemptsRes.data?.results || []);
          const availableTestIdSet = new Set(transformedTests.map(t => normalizeTestId(t.id)));
          const isSubmittedAttempt = (a) => a.submitted === true || String(a.status || '').toUpperCase() === 'SUBMITTED';
          const submittedTestIdSet = new Set(attemptsList.filter(isSubmittedAttempt).map(a => normalizeTestId(a.test ?? a.test_id)));
          const pendingAssignments = assignmentsList.filter(asg => !submittedTestIdSet.has(normalizeTestId(asg.test)));

          const qCount = (asg) => (typeof asg.question_count === 'number' && asg.question_count > 0) ? asg.question_count : 0;
          const assignmentByTestId = assignmentsList.reduce((acc, asg) => { acc[normalizeTestId(asg.test)] = asg; return acc; }, {});

          const supplement = pendingAssignments
            .filter(asg => !availableTestIdSet.has(normalizeTestId(asg.test)))
            .map(asg => ({
              id: asg.test,
              title: asg.test_title || 'Untitled Test',
              subject_name: asg.subject_name,
              subject: asg.subject_name,
              topics_list: Array.isArray(asg.topics_list) ? asg.topics_list : (asg.topic_name ? [asg.topic_name] : []),
              status: 'not-started',
              has_submitted_attempt: false,
              dueDate: asg.target_date,
              displayDate: asg.target_date,
              target_date: asg.target_date,
              dateLabel: 'Due On',
              totalQuestions: qCount(asg),
              question_count: qCount(asg),
              num_questions: qCount(asg),
              assigned_by_id: asg.assigned_by_id ?? asg.assigned_by?.id,
              assigned_by_email: asg.assigned_by_email ?? asg.assigned_by?.email,
              assigned_by_first_name: asg.assigned_by_first_name ?? asg.assigned_by?.first_name,
              _from_assignments: true,
            }));
          setAssignmentsSupplement(supplement);

          const completedFromAttempts = attemptsList
            .filter(a => isSubmittedAttempt(a) && !availableTestIdSet.has(normalizeTestId(a.test ?? a.test_id)))
            .map(a => {
              const testId = a.test ?? a.test_id;
              const asg = assignmentByTestId[normalizeTestId(testId)];
              return {
                id: testId,
                title: a.test_title || asg?.test_title || 'Untitled Test',
                subject_name: asg?.subject_name,
                subject: asg?.subject_name,
                topics_list: Array.isArray(asg?.topics_list) ? asg.topics_list : (asg?.topic_name ? [asg.topic_name] : []),
                status: 'completed',
                attempt_status: 'completed',
                has_submitted_attempt: true,
                completedDate: a.end_time,
                displayDate: a.end_time,
                dueDate: asg?.target_date,
                dateLabel: 'Completed On',
                totalQuestions: asg ? qCount(asg) : 0,
                question_count: asg ? qCount(asg) : 0,
                attempt_id: a.id,
                attemptId: a.id,
                assigned_by_id: asg?.assigned_by_id ?? asg?.assigned_by?.id,
                assigned_by_email: asg?.assigned_by_email ?? asg?.assigned_by?.email,
                assigned_by_first_name: asg?.assigned_by_first_name ?? asg?.assigned_by?.first_name,
                _from_completed_supplement: true,
              };
            });
          setCompletedSupplement(completedFromAttempts);

          if (completedFromAttempts.length > 0 && userId) {
            const BATCH_SIZE = 5;
            const results = [];
            for (let i = 0; i < completedFromAttempts.length; i += BATCH_SIZE) {
              const batch = completedFromAttempts.slice(i, i + BATCH_SIZE);
              const batchResults = await Promise.all(batch.map(async (test) => {
                try {
                  const [uploads, gradingResponse] = await Promise.all([
                    uploadService.listUploads({ studentId: userId, testId: test.id, role: 'student' }).catch(() => []),
                    test.attempt_id ? api.get(`${TEST_ATTEMPT_NOTE}?test_attempt=${test.attempt_id}`).catch(() => ({ data: { is_marked: false } })) : Promise.resolve({ data: { is_marked: false } }),
                  ]);
                  const uploadsArray = Array.isArray(uploads) ? uploads : (uploads?.results || []);
                  return { testId: test.id, uploads: uploadsArray, isMarked: gradingResponse?.data?.is_marked || false };
                } catch {
                  return { testId: test.id, uploads: [], isMarked: false };
                }
              }));
              results.push(...batchResults);
            }
            setTestUploads(prev => {
              const next = { ...prev };
              results.forEach(({ testId, uploads, isMarked }) => {
                next[testId] = uploads;
                const t = completedFromAttempts.find(c => c.id === testId);
                if (t) t.is_marked = isMarked;
              });
              return next;
            });
          }
        } catch (_) {
          setAssignmentsSupplement([]);
          setCompletedSupplement([]);
        }

        setError(null);
      } catch (err) {

        setError('Failed to load tests. Please try again.');
        setSnackbar({
          open: true,
          message: 'Failed to load tests. Please try again.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTests();
  }, [activeTab, refresh]);

  // Fetch LOVs for auto test generation
  const fetchLOVs = async () => {
    try {
      const [subRes, yearRes, boardRes, topicRes] = await Promise.all([
        testService.getSubjects(),
        testService.getYears(),
        testService.getBoards(),
        testService.getTopics()
      ]);
      
      setSubjects(Array.isArray(subRes) ? subRes : (subRes?.results || []));
      setYears(filterPublished(Array.isArray(yearRes) ? yearRes : (yearRes?.results || [])));
      setBoards(filterPublished(Array.isArray(boardRes) ? boardRes : (boardRes?.results || [])));
      setAllTopics(Array.isArray(topicRes) ? topicRes : (topicRes?.results || []));
      setTopicOptions(Array.isArray(topicRes) ? topicRes : (topicRes?.results || []));
    } catch (error) {

    }
  };

  // Fetch LOVs on component mount
  useEffect(() => {
    fetchLOVs();
  }, []);

  // Handle opening the results dialog (memoized so TestCard memo works)
  const handleOpenResults = useCallback(async (test) => {
    try {
      navigate(`/test-results/${test.attempt_id || test.attemptId}`);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to open test results. Please try again.',
        severity: 'error'
      });
    }
  }, [navigate]);
  
  // Handle closing the results dialog
  const handleCloseResults = () => {
    setResultsDialog({
      open: false,
      loading: false,
      results: [],
      testTitle: '',
      page: 0,
      rowsPerPage: 10
    });
  };

  // Handle page change in results table
  const handleChangePage = (event, newPage) => {
    setResultsDialog(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Handle rows per page change in results table
  const handleChangeRowsPerPage = (event) => {
    setResultsDialog(prev => ({
      ...prev,
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10)
    }));
  };

  // Handle starting a test (test may be from main list, assignmentsSupplement, or completedSupplement)
  const handleStartTest = (testId) => {
    const matchId = (t) => t.id === testId || String(t.id) === String(testId);
    const selectedTest = tests.find(matchId) ?? assignmentsSupplement.find(matchId) ?? completedSupplement.find(matchId);
    if (!selectedTest) {
      setSnackbar({ open: true, message: 'Test not found. Please refresh and try again.', severity: 'error' });
      return;
    }
  
    const ongoing = selectedTest.ongoing_attempt;
    
    
    // Determine if test is practice mode
    const isPracticeMode = selectedTest?.exam_mode?.mode?.toLowerCase() === 'practice' || 
                          !selectedTest?.exam_mode; // Default to practice if no exam_mode set
    const testModeInfo = {
      testId: selectedTest.id,
      exam_mode: selectedTest?.exam_mode,
      exam_mode_mode: selectedTest?.exam_mode?.mode,
      test_type: selectedTest?.test_type,
      isPracticeMode,
      ongoing: !!ongoing
    };
  
    // Only show resume dialog for ongoing EXAM tests, not practice tests
    if (ongoing && !isPracticeMode) {

      setResumeDialog({ open: true, test: selectedTest, ongoing });
      return;
    }
    
    if (ongoing && isPracticeMode) {

    }
  
    navigate(`/test/${testId}/instructions`, {
      state: { 
        testTitle: selectedTest.title,
        test: selectedTest  // Pass test data to determine mode and ongoing status
      }
    });
  };
  
  // Handle refresh
  const handleRefresh = () => {
    setRefresh(prev => !prev);
  };

  // Download worksheet for a completed test (memoized so TestCard memo works)
  const handleDownloadWorksheet = useCallback(async (test) => {
    try {
      await testService.downloadTestWorksheet(test.id, test.title || 'worksheet');
      setSnackbar({ open: true, message: 'Worksheet downloaded successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.status ? `Failed to download worksheet: ${err.response.status}` : (err.message || 'Error downloading worksheet.'),
        severity: 'error',
      });
    }
  }, []);

  // Upload dialog handlers (memoized so TestCard memo works)
  const handleOpenUpload = useCallback((test) => {
    const testId = typeof test === 'object' ? test?.id : test;
    const attemptId = typeof test === 'object' ? (test?.attempt_id ?? test?.attemptId) : null;
    setUploadDialog({ open: true, testId, attemptId });
  }, []);

  // Check worksheet: open PDF annotation dialog directly (Graded by Teacher tab)
  const handleOpenCheckWorksheet = async (test) => {
    const attemptId = test.attempt_id || test.attemptId;
    if (!studentId || !test.id || !attemptId) {
      setSnackbar({ open: true, message: 'Missing test or attempt data.', severity: 'error' });
      return;
    }
    try {
      const [uploads, attemptRes, revRes] = await Promise.all([
        uploadService.listUploads({ studentId, testId: test.id, role: 'student' }),
        api.get(`${ATTEMPTS}${attemptId}/`),
        api.get(REVISIONS).catch(() => ({ data: [] })),
      ]);
      const uploadsList = Array.isArray(uploads) ? uploads : (uploads?.results || []);
      const pdfUpload = uploadsList.find(u => (u.filename || '').toLowerCase().endsWith('.pdf'));
      if (!pdfUpload) {
        setSnackbar({ open: true, message: 'No PDF worksheet found.', severity: 'warning' });
        return;
      }
      const attemptData = attemptRes?.data || {};
      const answers = attemptData?.answers || [];
      const questions = answers.map((ans) => {
        const q = ans.question_details || {};
        return {
          id: q.id ?? ans.question_id,
          text: q.text ?? ans.question_text ?? 'Question text not available',
          question_type: q.type ?? ans.question_type,
          options: q.options ?? ans.question_options ?? [],
          correct_answer: q.correct_answer ?? ans.correct_answer,
          created_by: q.created_by ?? ans.question_created_by,
          user_answer: ans.answer ?? ans.user_answer ?? '',
          has_subquestions: q.has_subquestions ?? false,
          sub_questions: q.sub_questions ?? [],
          sub_answers: ans.sub_answers ?? [],
        };
      });
      const revArray = Array.isArray(revRes.data) ? revRes.data : (revRes.data?.results || []);
      const revisionData = revArray.filter((r) => r.test_attempt == attemptId);
      const gradingDone = test.is_marked === true || test.is_marked === 'Yes' || test.is_marked === 'YES' ||
        test.grading_completed === true || test.grading_completed === 'Yes' || test.grading_completed === 'YES';
      setCheckWorksheetDialog({
        open: true,
        uploadId: pdfUpload.id,
        filename: pdfUpload.filename,
        attemptId,
        questions,
        revisionData,
        gradingDone: !!gradingDone,
      });
    } catch (err) {
      setSnackbar({ open: true, message: err?.message || 'Failed to load worksheet.', severity: 'error' });
    }
  };

  const handleCloseUpload = async () => {
    const closedTestId = uploadDialog.testId;
    setUploadDialog({ open: false, testId: null, attemptId: null });
    if (studentId && closedTestId) {
      try {
        const uploads = await uploadService.listUploads({
          studentId,
          testId: closedTestId,
          role: 'student'
        });
        const uploadsArray = Array.isArray(uploads) ? uploads : (uploads?.results || []);
        setTestUploads(prev => ({ ...prev, [closedTestId]: uploadsArray }));
      } catch {
        setTestUploads(prev => ({ ...prev, [closedTestId]: [] }));
      }
    }
  };

  // Auto test creation handler
  const handleAutoTestCreate = async (formData) => {
    // Check if this is a validation error
    if (formData.error) {
      setSnackbar({
        open: true,
        message: formData.error,
        severity: 'error'
      });
      return;
    }

    try {
      // Create the test using auto-generation endpoint
      const createdTest = await testService.createAutoTest(formData);
      
      if (createdTest && createdTest.id) {
        // Auto-assign the test to the student
        await testService.assignTest(createdTest.id);
        
        setSnackbar({
          open: true,
          message: 'Test created and assigned successfully!',
          severity: 'success'
        });
        
        // Refresh the tests list
        setRefresh(prev => !prev);
        
        // Close the dialog
        setOpenAutoDialog(false);
      }
    } catch (error) {

      setSnackbar({
        open: true,
        message: 'Failed to create and assign test. Please try again.',
        severity: 'error'
      });
    }
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const hasWorksheetUploaded = (test) => {
    if (!test) return false;
    if (test.has_upload === true) return true;
    return (testUploads[test?.id] || []).length > 0;
  };
  const isGradedByTeacher = (test) => {
    if (!test) return false;
    if (test.is_marked === true || test.is_marked === 'Yes' || test.is_marked === 'YES') return true;
    const s = String(test.status || '').toUpperCase();
    const a = String(test.attempt_status || '').toUpperCase();
    return s === 'GRADED' || a === 'GRADED' || test.grading_completed === true || test.grading_completed === 'Yes' || test.grading_completed === 'YES';
  };

  const getCompletedDate = (test) => {
    if (!test) return new Date(0);
    const d = test.completedDate || test.displayDate || test.completed_date || test.completed_at || test.end_time || test.submitted_at;
    return d ? new Date(d) : new Date(0);
  };

  // Filter tests based on active tab; include supplement lists so subquestion/submitted tests appear correctly
  const filteredTests = useMemo(() => {
    const filtered = tests.filter(test => {
      if (activeTab === 'all') return true;
      if (activeTab === 'assigned') return !isTestCompleted(test);
      if (activeTab === 'worksheet-uploaded') return isTestCompleted(test) && hasWorksheetUploaded(test) && !isGradedByTeacher(test);
      if (activeTab === 'graded-by-teacher') return isTestCompleted(test) && isGradedByTeacher(test);
      if (activeTab === 'completed') return isTestCompleted(test) && !hasWorksheetUploaded(test) && !isGradedByTeacher(test);
      return true;
    });

    if (activeTab === 'completed' || activeTab === 'worksheet-uploaded') {
      filtered.sort((a, b) => getCompletedDate(b) - getCompletedDate(a));
    }

    const apiIdSet = new Set(filtered.map(t => normalizeTestId(t.id)));
    const dedupeSupplement = (list) => list.filter(s => !apiIdSet.has(normalizeTestId(s.id)));

    if (activeTab === 'assigned') {
      return assignmentsSupplement.length > 0 ? [...filtered, ...assignmentsSupplement] : filtered;
    }
    if (activeTab === 'all') {
      const pendingOnly = dedupeSupplement(assignmentsSupplement);
      const completedOnly = dedupeSupplement(completedSupplement);
      const combined = [...filtered, ...pendingOnly, ...completedOnly];
      return combined.length > filtered.length ? combined : filtered;
    }
    if (activeTab === 'completed') {
      const completedOnly = completedSupplement.filter(s => !hasWorksheetUploaded({ id: s.id }) && !isGradedByTeacher(s));
      const toAdd = dedupeSupplement(completedOnly);
      return toAdd.length > 0 ? [...filtered, ...toAdd] : filtered;
    }
    if (activeTab === 'worksheet-uploaded') {
      const withUploadsNotGraded = completedSupplement.filter(s => hasWorksheetUploaded({ id: s.id }) && !isGradedByTeacher(s));
      const toAdd = dedupeSupplement(withUploadsNotGraded);
      return toAdd.length > 0 ? [...filtered, ...toAdd] : filtered;
    }
    if (activeTab === 'graded-by-teacher') {
      const graded = completedSupplement.filter(isGradedByTeacher);
      const toAdd = dedupeSupplement(graded);
      return toAdd.length > 0 ? [...filtered, ...toAdd] : filtered;
    }
    return filtered;
  }, [tests, activeTab, testUploads, assignmentsSupplement, completedSupplement]);

  // --- Filters ---
  const [filters, setFilters] = useState({ assignedBy: '' });
  
  // helper to derive email of person who assigned the test
  const getAssignedByEmail = (t) => {
     return (
       t.assigned_by_email ||
       (t.assigned_by && t.assigned_by.email) ||
       (t.assigned_by_user && t.assigned_by_user.email) ||
       t.assigned_by_user_email ||
       
       t.teacher_email ||
       (t.created_by && t.created_by.email) ||
       t.created_by_email ||
       ''
     );
  };

  const assignedByOptions = useMemo(()=>{
     return Array.from(new Set(tests.map(getAssignedByEmail).filter(Boolean)));
  }, [tests]);

  // Handle filter dropdown change
  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  // Derive list after filters applied
  const displayTests = useMemo(() => {
    let list = filteredTests;
    if (filters.assignedBy) {
      list = list.filter(t => getAssignedByEmail(t) === filters.assignedBy);
    }
    return list;
  }, [filteredTests, filters]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleRefresh}
          startIcon={<RefreshIcon />}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Container>
    );
  }

    return (
    <StudentLayout>
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2 } }}>
        <Box sx={{ mb: { xs: 2, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }} size="medium" aria-label="Go back">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                My Tests
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {displayTests.length === 1 ? '1 test' : `${displayTests.length} tests`}
              </Typography>
            </Box>
          </Box>

            {/* Assign Test Button */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setOpenAutoDialog(true)}
                fullWidth={false}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  minHeight: 44,
                }}
              >
                Assign Test
              </Button>
            </Box>

            {/* Tabs - scrollable on mobile */}
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 2, minHeight: 48, '& .MuiTab-root': { minHeight: 48 } }}
          >
            <Tab label="All Tests" value="all" />
            <Tab label="Assigned" value="assigned" />
            <Tab label="Completed" value="completed" />
            <Tab label="Worksheet Uploaded" value="worksheet-uploaded" />
            <Tab label="Graded by Teacher" value="graded-by-teacher" />
          </Tabs>

          {/* Layout: stack on mobile, side-by-side on md+ */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 0 }}>
            {/* Filter Card - full width on mobile */}
            <Paper elevation={1} sx={{ p: 2, width: { xs: '100%', md: 260 }, mr: { xs: 0, md: 3 }, mb: { xs: 2, md: 0 }, flexShrink: 0 }}>
              <Typography variant="subtitle1" gutterBottom>Filters</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField select label="Assigned By" size="small" fullWidth value={filters.assignedBy} onChange={handleFilterChange('assignedBy')}>
                  <MenuItem value="">All</MenuItem>
                  {assignedByOptions.map(email=>(<MenuItem key={email} value={email}>{email}</MenuItem>))}
                </TextField>
              </Box>
            </Paper>
            {/* Main content */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {/* Test List */}
              <Grid container spacing={2}>
                {displayTests.length > 0 ? (
                  displayTests.map((test) => (
                    <Grid item xs={12} key={test.id}>
                      <TestCard
                        test={test}
                        studentId={studentId}
                        activeTab={activeTab}
                        onOpenResults={handleOpenResults}
                        onOpenUpload={handleOpenUpload}
                        onStartTest={handleStartTest}
                        onDownloadWorksheet={activeTab === 'completed' ? handleDownloadWorksheet : undefined}
                        hasWorksheet={hasWorksheetUploaded(test)}
                      />
                    </Grid>
                  ))
                 ) : (
                   <Grid item xs={12}>
                     <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
                       <HourglassEmptyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                       <Typography variant="h6" color="textSecondary" gutterBottom>
                         No tests found
                       </Typography>
                       <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                         {activeTab === 'completed' 
                           ? 'You have not completed any tests yet.'
                           : activeTab === 'worksheet-uploaded'
                           ? 'You have not uploaded worksheets for any tests yet.'
                           : activeTab === 'graded-by-teacher'
                           ? 'No tests have been graded by your teacher yet.'
                           : 'There are no tests available at the moment.'}
                       </Typography>
                       <Button 
                         variant="outlined" 
                         color="primary" 
                         onClick={handleRefresh}
                         startIcon={<RefreshIcon />}
                       >
                         Refresh
                       </Button>
                     </Paper>
                   </Grid>
                 )}
               </Grid>
             </Box>
           </Box>
        </Box>

        {/* Results Dialog */}
        <Dialog 
          open={resultsDialog.open} 
          onClose={handleCloseResults}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <span>Test Results: {resultsDialog.testTitle}</span>
              <IconButton onClick={handleCloseResults} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {resultsDialog.loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Question</TableCell>
                      <TableCell>Your Answer</TableCell>
                      <TableCell>Correct Answer</TableCell>
                      <TableCell align="right">Marks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultsDialog.results
                      .slice(
                        resultsDialog.page * resultsDialog.rowsPerPage,
                        resultsDialog.page * resultsDialog.rowsPerPage + resultsDialog.rowsPerPage
                      )
                      .map((result, index) => (
                        <TableRow 
                          key={index}
                          sx={{ 
                            '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                            backgroundColor: result.isCorrect ? 'success.light' : 'error.light'
                          }}
                        >
                          <TableCell><MathRenderer content={result.question || ''} variant="body2" /></TableCell>
                          <TableCell><MathRenderer content={result.studentAnswer ?? ''} variant="body2" /></TableCell>
                          <TableCell><MathRenderer content={result.correctAnswer ?? ''} variant="body2" /></TableCell>
                          <TableCell align="right">
                            {result.marksAwarded} / {result.maxMarks}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={resultsDialog.results.length}
                  rowsPerPage={resultsDialog.rowsPerPage}
                  page={resultsDialog.page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseResults} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Upload Modal */}
        {uploadDialog.open && (
          <UploadModal
            open={uploadDialog.open}
            onClose={handleCloseUpload}
            testId={uploadDialog.testId}
            attemptId={uploadDialog.attemptId}
            studentId={studentId}
            showTeacherUploads
            canAnnotate
          />
        )}

        {/* Check worksheet: PDF viewer + read-only grading (opens from Graded by Teacher tab) */}
        <PdfAnnotationViewer
          open={checkWorksheetDialog.open}
          onClose={() => setCheckWorksheetDialog({ open: false, uploadId: null, filename: null, attemptId: null, questions: [], revisionData: [], gradingDone: false })}
          uploadId={checkWorksheetDialog.uploadId}
          filename={checkWorksheetDialog.filename}
          role="student"
          readOnlyAnnotations
          gradingDone={checkWorksheetDialog.gradingDone}
          attemptId={checkWorksheetDialog.attemptId}
          questions={checkWorksheetDialog.questions}
          revisionData={checkWorksheetDialog.revisionData}
          currentUserId={user?.id}
        />

        {/* Resume Dialog */}
        <Dialog open={resumeDialog.open} onClose={() => setResumeDialog({ open: false, test: null, ongoing: null })}>
          <DialogTitle>Resume Test?</DialogTitle>
          <DialogContent>
            <Typography>
              The test will start from where it ended.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={downloadingWorksheet ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
              onClick={handleDownloadWorksheetFromResume}
              disabled={downloadingWorksheet}
            >
              {downloadingWorksheet ? 'Downloading...' : 'Download Worksheet'}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleResumeCancel}>Exit</Button>
            <Button onClick={handleResumeConfirm} variant="contained" color="primary">
              Resume
            </Button>
          </DialogActions>
        </Dialog>

        {/* Auto Test Generation Dialog */}
        <AutoTestDialog
          open={openAutoDialog}
          onClose={() => setOpenAutoDialog(false)}
          onSave={handleAutoTestCreate}
          subjects={subjects}
          years={years}
          boards={boards}
          topics={topicOptions}
          showMyQuestions={false}
          title="Auto-Generate Test for Self-Practice"
          saveButtonText="Generate & Assign"
          user={user}
          initialForm={{
            title: '',
            subject: '',
            topics: [],
            test_type: TEST_TYPES.SKILL_CHECK,
            board: '',
            academic_year: '',
            num_questions: 10,
            duration_minutes: 60,
            percent_basic: 50,
            percent_advanced: 50,
            note: 'Auto-generated test for self-practice',
          }}
        />
             </Container>
     </StudentLayout>
   );
 };
 
 export default Tests;
